import { Backdrop, Drawer } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { noop } from 'lodash-es';
import { useConfigContext } from '../../config/ConfigContext';
import { useSsoSessionContext } from '../../Session';
import { RatePlan, Subscription } from '../services/types';

declare global {
  interface Window {
    SS_WIDGET_TOKEN?: string;
    SS_ACCOUNT?: string;
    SS_SURVEY_NAME?: string;
    sparrowConfig?: { triggerOnLoad: false; newTab: true };
    SparrowLauncher: (
      type: 'popup',
      options: {
        variables: { licenseid: string; subnum: string; subexp: string; user_email: string };
      },
    ) => void;
  }
}

export interface SurveyProps {
  ratePlan: RatePlan;
  subscription: Subscription;
  onComplete?: () => void;
}

export const Survey: React.FC<SurveyProps> = ({ ratePlan, subscription, onComplete = noop }) => {
  const [surveyRef, setSurveyRef] = useState<HTMLDivElement | null>(null);

  const { ssoSession } = useSsoSessionContext();
  const userEmail = ssoSession?.email || '';
  useSurveySparrowWidget(ratePlan, subscription, userEmail);
  useDimensionsOverride(surveyRef);
  useWindowMessageCallback('surveyCompleted', onComplete);
  useWindowMessageCallback('redirectPage', onComplete);

  return (
    <Backdrop open={true}>
      <Drawer open={true} anchor='right' PaperProps={{ style: { minWidth: '500px' } }}>
        <div ref={setSurveyRef} id='ss_survey_widget' />
      </Drawer>
    </Backdrop>
  );
};

function useSurveySparrowWidget(ratePlan: RatePlan, subscription: Subscription, userEmail: string) {
  const { token, name } = useSurvey(ratePlan);
  const licenseId = subscription.License_Id__c;
  const subscriptionNumber = subscription.subscriptionNumber;
  const subscriptionEndDate = subscription.termEndDate;

  useEffect(() => localStorage.removeItem(`removed-ss-widget-${token}`), [token]);

  useEffect(() => {
    const scr = document.createElement('script');

    window.SS_ACCOUNT = 'enscape.surveysparrow.com';
    window.SS_SURVEY_NAME = name;
    window.SS_WIDGET_TOKEN = token;
    window.sparrowConfig = { triggerOnLoad: false, newTab: true };

    scr.src = `https://enscape.surveysparrow.com/widget/${token}`;
    scr.id = 'ss-widget';

    scr.addEventListener('load', () =>
      window.SparrowLauncher('popup', {
        variables: {
          licenseid: licenseId,
          subnum: subscriptionNumber,
          subexp: subscriptionEndDate,
          user_email: userEmail,
        },
      }),
    );

    document.body.appendChild(scr);

    return () => void scr.parentElement?.removeChild(scr);
  }, [name, token, licenseId, subscriptionNumber, subscriptionEndDate, userEmail]);
}

export function useSurvey(ratePlan: RatePlan) {
  const { annualSurvey, monthlySurvey } = useConfigContext()?.surveys || {};

  return ratePlan.ratePlanName.includes('Yearly') ? { ...annualSurvey } : { ...monthlySurvey };
}

function useDimensionsOverride(el?: HTMLElement | null) {
  useEffect(() => {
    if (!el) {
      return;
    }

    const mo = new MutationObserver(() => {
      el.style.height = '100%';
      el.style.width = '100%';

      mo.disconnect();
    });

    mo.observe(el, { attributes: true, attributeFilter: ['style'] });

    return () => void mo.disconnect();
  }, [el]);
}

function useWindowMessageCallback(messageType: string, callback: () => void) {
  useEffect(() => {
    const eventHandler = (e: MessageEvent) => e.data?.type === messageType && void callback();

    window.addEventListener('message', eventHandler);

    return () => void window.removeEventListener('message', eventHandler);
  }, [callback, messageType]);
}
